import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { ConfirmDialogComponent } from '../../../common/components/confirm-dialog/confirm-dialog';
import { CurrentUser } from "../../../common/providers/current-user";
import { ModuleMenu } from "../../../common/models/menu";
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { SessionRow, SessionsProvider } from '../../providers/sessions-provider';
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { FilesProvider } from '../../../files/providers/files-provider';
import { LibraryService } from '../../../libraries/providers/library.services';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { SessionRights } from '../../providers/session-rights';
import { fromEvent, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { ErrorManagerService } from '../../../common/providers/error-manager-service';
import { SessionDatasource } from '../../../common/datasource/session.datasource';
import { debounceTime, distinctUntilChanged, tap, filter } from 'rxjs/operators';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { Library } from "../../../common/models/library";
import { User } from "../../../common/models/user";
import { LibrariesSideMenuComponent } from "../../../libraries/components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { MatInput, MatInputModule } from "@angular/material/input";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { AsyncPipe, NgIf } from "@angular/common";
import { MatTable, MatTableModule } from "@angular/material/table";
import { TableHistogramChartComponent } from "../../../common/components/table-histogram-chart/table-histogram-chart";
import { DurationVerbosePipe } from "../../../common/pipes/duration-verbose";
import { MatMenu, MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatIconButton } from "@angular/material/button";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";

@Component({
  selector: 'sessions-list-page',
  providers: [],
  standalone: true,
  styleUrls: ['./list.scss'],
  imports: [
    LibrariesSideMenuComponent,
    Error401Component,
    Error404Component,
    ModuleContentComponent,
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    ReactiveFormsModule,
    NgIf,
    MatTableModule,
    TableHistogramChartComponent,
    DurationVerbosePipe,
    MatIconButton,
    MatMenuModule,
    MatPaginator,
    CenterLoaderComponent,
    AsyncPipe

  ],
  templateUrl: './list.html'
})
export class SessionsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public pageTitle = 'SessionsList';
  public mainActions: ModuleMenu[] = [];
  public isLoading = true;
  public is401 = false;
  public is404 = false;

  public organization: Organization = {} as Organization;
  public library: Library = {} as Library;
  public rights: SessionRights = new SessionRights(this._utils);

  public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
  public columns: string[] = ['name', 'adn', 'totalTime', 'tags', 'creater', 'actions'];
  public backToUrl: string | null = null;

  public paginationPageItemNumber = 10;

  public sessionDataSource: SessionDatasource = {} as SessionDatasource;

  @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
  @ViewChild(MatSort, {static: false}) public sorter: MatSort | undefined;
  @ViewChild('search', {static: false}) public search: ElementRef | undefined;
  private destroy$ = new Subject<void>();

  private _displayContext = 0;
  private subscriptions: Subscription[] = [];

  constructor(
    public conf: ConfigProvider,
    public breadcrumbProvider: BreadcrumbProvider,
    public currentUser: CurrentUser,
    private _utils: UtilsProvider,
    private _title: Title,
    private _router: Router,
    private _toaster: MatSnackBar,
    private _currentUserRoles: CurrentUserRolesProvider,
    private _route: ActivatedRoute,
    private _translater: TranslateService,
    private _dialog: MatDialog,
    private _sessionsProvider: SessionsProvider,
    private errorManagerService: ErrorManagerService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngOnInit(): void {
    this._route.data.subscribe((data: any) => { //{ organization: Organization; library: Library }
      this.organization = data.organization;
      this.library = data.library;

      this.createPageTitle(data);
      this.createBreadcrumb();

      this.rights.evaluateScreenRights({
        rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
      });

      if (!this.rights.LIST) {
        this.is401 = true;
        return;
      }

      if (this.rights.ADD) {
        this.mainActions = [
          { title: 'Add', icon: 'add', url: null, onClick: () => this.onAddSessionClicked() }
        ];
      }

      this.sessionDataSource = new SessionDatasource(this._sessionsProvider);
      const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop()

      const loadObservable$ = data.context === LibraryService.CONTEXT_USER
        ? this.sessionDataSource.loadSessionRowsByUser(this.library.id!, this.organization, this.currentUser.user!, 1, this.paginationPageItemNumber)
        : this.sessionDataSource.loadSessionRows(this.library.id!, this.organization, 1, this.paginationPageItemNumber, '', filterKey);

      this.subscriptions.push(loadObservable$.subscribe({
        next: () => {
          this.updateTableData();
          this.isLoading = false;
        },
        error: (err: any) => {
          this.onError(err);
        }
      }));
    });
  }

  public ngAfterViewInit() {
    // trigger aux keys inputs
    if (this.search && this.search.nativeElement) {
      fromEvent<KeyboardEvent>(this.search.nativeElement, 'keyup').pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.key === 'Enter' || e.keyCode === 13),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      ).subscribe((searchTerm) => {
        this.triggerSearch();
      });
    } else {
      console.error('Search input not found');
    }

    this.paginator?.page.subscribe(() => this.loadSessionPaginated());
  }

  protected triggerSearch() {
    this.paginator?.firstPage();
    this.loadSessionPaginated();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public onAddSessionClicked(): void {
    switch (this._displayContext) {
      case LibraryService.CONTEXT_ORGANIZATION:
        this._router.navigate([this.organization!.code, 'libraries', this.library.id, 'sessions', 0, 'editor'], {
          queryParams: {
            backto: this.backToUrl,
            libraryId: this.library.id,
          }
        });
        break;
      case LibraryService.CONTEXT_USER:
        this._router.navigate([this.organization!.code, 'sessions', 0, 'editor'], {
          queryParams: {
            backto: this.backToUrl,
            libraryId: this.library.id,
          }
        });
        break;
    }
  }

  public onDuplicateSessionClicked(toDuplicate: SessionRow): void {
    const currentPath = this._router.url;
    let routeSegments: any[];

    if (currentPath.includes('sessions-elitment')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions-elitment', toDuplicate.id, 'editor'];
    } else if (currentPath.includes('sessions-super-coach')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions-super-coach', toDuplicate.id, 'editor'];
    } else if (currentPath.includes('sessions/personal')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions', 'personal', toDuplicate.id, 'editor'];
    } else {
      // Fallback to personal sessions
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions', 'personal', toDuplicate.id, 'editor'];
    }

    this._router.navigate(routeSegments, {
      queryParams: {
        duplicate: true,
        backto: this.backToUrl,
        libraryId: this.library.id,
      }
    });
  }

  public onEditSessionClicked(toEdit: SessionRow) {
    const currentPath = this._router.url;
    let routeSegments: any[];

    if (currentPath.includes('sessions-elitment')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions-elitment', toEdit.id, 'editor'];
    } else if (currentPath.includes('sessions-super-coach')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions-super-coach', toEdit.id, 'editor'];
    } else if (currentPath.includes('sessions/personal')) {
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions', 'personal', toEdit.id, 'editor'];
    } else {
      // Fallback to personal sessions
      routeSegments = [this.organization!.code, 'libraries', this.library.id, 'sessions', 'personal', toEdit.id, 'editor'];
    }

    this._router.navigate(routeSegments, {
      queryParams: {
        backto: this.backToUrl,
        libraryId: this.library.id,
      }
    });
  }

  public onDeleteSessionClicked(toDelete: SessionRow): void {

    const dialog: MatDialogRef<any> = this._dialog.open(ConfirmDialogComponent, {
      data: {
        confirmText: this._translater.instant('Confirm_delete_session')
      }
    });
    this.subscriptions.push(
      dialog.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.isLoading = true;
          this.deleteSession(toDelete).subscribe({
            next: () => {
              this.loadSessionPaginated();
              this._toaster.open(this._translater.instant('Session_deleted'), '', { duration: 5000 });
            },
            error: (error: any) => this.onError(error)
          });
        }
      })
    );
  }

  public onRowClicked(row: SessionRow): void {
    const currentPath = this._router.url;
    let url: string | null = null;

    if (currentPath.includes('sessions-elitment')) {
      url = '/' + this.organization!.code + '/libraries/' + this.library.id + '/sessions-elitment/' + row.id;
    } else if (currentPath.includes('sessions-super-coach')) {
      url = '/' + this.organization!.code + '/libraries/' + this.library.id + '/sessions-super-coach/' + row.id;
    } else if (currentPath.includes('sessions/personal')) {
      url = '/' + this.organization!.code + '/libraries/' + this.library.id + '/sessions/personal/' + row.id;
    } else {
      // Fallback to personal sessions
      url = '/' + this.organization!.code + '/libraries/' + this.library.id + '/sessions/personal/' + row.id;
    }

    this._router.navigateByUrl(url);
  }

  public onSortChange(event: any): void {
    this.dataSource.sortBy(event.active, event.direction);
  }

  private deleteSession(toDelete: SessionRow): Observable<any> {
    return this._sessionsProvider.deleteSessionRow(this.organization.id!, toDelete);
  }

  private createPageTitle(data: any) {
    // Detect the current route context
    const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop();

    if (filterKey === 'sessions-elitment') {
      this._displayContext = LibraryService.CONTEXT_ORGANIZATION;
      this.pageTitle = this._translater.instant('Elitment_sessions_title');
    } else if (filterKey === 'sessions-super-coach') {
      this._displayContext = LibraryService.CONTEXT_ORGANIZATION;
      this.pageTitle = this._translater.instant('Super_coaches_sessions_title');
    } else if (filterKey === 'personal' || data.context === LibraryService.CONTEXT_USER) {
      this._displayContext = LibraryService.CONTEXT_USER;
      this.pageTitle = this._translater.instant('Personal_sessions_title');
    } else {
      // Fallback to old behavior
      if (data.context === LibraryService.CONTEXT_USER) {
        this._displayContext = LibraryService.CONTEXT_USER;
        this.pageTitle = this._translater.instant('My_sessions');
      } else {
        this._displayContext = LibraryService.CONTEXT_ORGANIZATION;
        this.pageTitle = this._translater.instant('Organization_sessions', { organization: this.organization.name });
      }
    }
    this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
  }

  private createBreadcrumb() {
    const crumbs: Crumb[] = [
      {
        shortTitle: this.pageTitle,
        title: this.pageTitle,
        url: this._router.url
      }
    ];
    this.breadcrumbProvider.setDefaultCrumbs(crumbs);
  }

  private onError(error: any): void {
    this.errorManagerService.error(error);
    this.isLoading = false;

    if (error.status === 404) {
      this.is404 = true;
    } else if (error.status === 401) {
      this.is401 = true;
    }
  }

  private loadSessionPaginated(): void {
    this.isLoading = true;
    const pageIndex = this.paginator!.pageIndex + 1;
    const pageSize = this.paginator!.pageSize;
    const keyword = this.search!.nativeElement.value;

    const filterKey = this._route.snapshot.routeConfig?.path?.split('/').pop()
    const isUserContext = this._route.snapshot.data['context'] === LibraryService.CONTEXT_USER;

    const loadObservable$: Observable<void> = isUserContext
      ? this.sessionDataSource.loadSessionRowsByUser(this.library.id!, this.organization, this.currentUser.user!, pageIndex, pageSize, keyword)
      : this.sessionDataSource.loadSessionRows(this.library.id!, this.organization, pageIndex, pageSize, keyword, filterKey);

    loadObservable$.subscribe({
      next: () => {
        this.updateTableData();
        this.isLoading = false;
      },
      error: (err: any) => {
        this.onError(err);
      }
    });
  }


  private updateTableData(): void {
    this.dataSource.data = this.sessionDataSource.subject.getValue();
    this.dataSource._updateChangeSubscription(); // Explicitly inform the table that the data has changed
    this.changeDetectorRef.detectChanges();
  }
}
